import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { MAPS_SEARCH_START, MAPS_SEARCH_STATUS } from './maps-graphql/queries';
import { MapsClientError } from './maps-error.service';
import type { MapsQualificationConfigEntry, MapsSearchJob, MapsSearchResult } from '../models/maps.types';

export interface SearchRequest {
    jobDescription: string;
    recruiterGuidance?: string;
    uploadIds?: string[];
    resultCount?: number;
    /** Modified qualification configuration (qualification-weighting panel Apply).
     *  Present → the server runs a configured rerun. */
    qualificationConfig?: MapsQualificationConfigEntry[];
}

/**
 * Async submit + short-poll search client. A cold search runs 2.5–4.5 min
 * server-side, but Azure App Service's front end cuts any single HTTP request
 * at ~240s (observed: stage run 7-28, XHR dropped at 240.3s while the server
 * finished at 252s). mapsSearchStart returns a poll handle immediately; each
 * mapsSearchStatus poll is a sub-second POST, so no request ever approaches
 * the platform limit.
 */
@Injectable({ providedIn: 'root' })
export class SearchService {
    private static readonly POLL_INTERVAL_MS = 4_000;
    /** Overall client cap; maps to SIMILARITY_TIMEOUT copy. Server-side runs
     *  are additionally swept at 60 min by the job registry. */
    private static readonly OVERALL_TIMEOUT_MS = 15 * 60_000;
    /** Consecutive transport failures tolerated (~20s of dead network). */
    private static readonly MAX_TRANSPORT_FAILURES = 5;

    constructor(private readonly apollo: Apollo) {}

    async Search(req: SearchRequest, onStage?: (stage: string) => void): Promise<MapsSearchResult> {
        const reqId = Math.random().toString(36).slice(2, 8);
        const t0 = performance.now();
        console.log(`[maps.search ${reqId}] starting async search`, {
            jdLength: req.jobDescription.length,
            guidanceLength: req.recruiterGuidance?.length ?? 0,
            qualificationCount: req.qualificationConfig?.length ?? 0,
        });
        // Map the client-side request onto the server's MapsSearchInput shape.
        // The panel's config rides in as `qualifications`; omit it entirely on a
        // default search so the server takes the unmodified path.
        const { qualificationConfig, ...rest } = req;
        const input = qualificationConfig?.length
            ? { ...rest, qualifications: qualificationConfig }
            : rest;
        const started = await firstValueFrom(
            this.apollo.mutate<{ mapsSearchStart: { searchRequestId: string } }>({
                mutation: MAPS_SEARCH_START,
                variables: { input },
            })
        );
        const searchRequestId = started.data?.mapsSearchStart?.searchRequestId;
        if (!searchRequestId) {
            throw new Error('mapsSearchStart returned empty payload');
        }
        console.log(`[maps.search ${reqId}] job ${searchRequestId} started in ${Math.round(performance.now() - t0)}ms`);
        try {
            const result = await this.pollUntilDone(searchRequestId, onStage);
            console.log(`[maps.search ${reqId}] completed in ${Math.round(performance.now() - t0)}ms`, {
                resultCount: result.results?.length ?? 0,
            });
            return result;
        } catch (err) {
            console.error(`[maps.search ${reqId}] failed after ${Math.round(performance.now() - t0)}ms`, err);
            throw err;
        }
    }

    private async pollUntilDone(
        searchRequestId: string,
        onStage?: (stage: string) => void
    ): Promise<MapsSearchResult> {
        const deadline = Date.now() + SearchService.OVERALL_TIMEOUT_MS;
        let transportFailures = 0;
        let lastStage: string | null = null;
        while (Date.now() < deadline) {
            await Delay(SearchService.POLL_INTERVAL_MS);
            let job: MapsSearchJob;
            try {
                const res = await firstValueFrom(
                    this.apollo.query<{ mapsSearchStatus: MapsSearchJob }>({
                        query: MAPS_SEARCH_STATUS,
                        variables: { searchRequestId },
                        fetchPolicy: 'network-only',
                    })
                );
                const status = res.data?.mapsSearchStatus;
                if (!status) {
                    throw new Error('mapsSearchStatus returned empty payload');
                }
                job = status;
                transportFailures = 0;
            } catch (err) {
                // GraphQL-level errors are terminal (UNAUTHENTICATED, SEARCH_REQUEST_NOT_FOUND);
                // transport blips (status 0 / 5xx) are tolerated up to the cap.
                if (CombinedGraphQLErrors.is(err)) throw err;
                transportFailures++;
                if (transportFailures >= SearchService.MAX_TRANSPORT_FAILURES) throw err;
                continue;
            }
            if (job.stage !== null && job.stage !== lastStage) {
                lastStage = job.stage;
                onStage?.(job.stage);
            }
            if (job.status === 'SUCCEEDED' && job.result !== null) {
                return job.result;
            }
            if (job.status === 'FAILED') {
                throw new MapsClientError(job.error?.code || 'INTERNAL_ERROR', job.error?.message);
            }
        }
        throw new MapsClientError('SIMILARITY_TIMEOUT');
    }
}

function Delay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
}
